import React, { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Form,
  Button,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiInformationOutline, mdiClockOutline, mdiMapMarker } from "@mdi/js";
import {
  setPropertyDetailsForm,
  setFormErrors,
  clearFormError,
  fetchBookingData,
  fetchDuration,
} from "../slice";
import { formatDuration } from "../utils";
import { HOUSE_TYPE_OPTIONS } from "../constants";

const StepPropertyDetails = () => {
  const dispatch = useDispatch();
  const { uniqueId } = useParams();

  const {
    contactForm,
    propertyDetailsForm,
    propertyDataFound,
    estimatedDurationMinutes,
    durationLoading,
    formErrors,
    bookingDataLoading,
    selectedTimezone,
  } = useSelector((state) => state.publicClientBooking);

  // Build address parts for display
  const addressLine1 = useMemo(() => {
    const parts = [contactForm.address1, contactForm.address2].filter(Boolean);
    return parts.join(", ");
  }, [contactForm.address1, contactForm.address2]);

  const addressLine2 = useMemo(() => {
    const parts = [
      contactForm.city,
      contactForm.state,
      contactForm.postalCode,
    ].filter(Boolean);
    return parts.join(", ");
  }, [contactForm.city, contactForm.state, contactForm.postalCode]);

  const handleFieldChange = (field, value) => {
    dispatch(setPropertyDetailsForm({ [field]: value }));
    if (formErrors[field]) {
      dispatch(clearFormError(field));
    }
  };

  const durationDebounceRef = useRef(null);

  const handleSquareFeetChange = (e) => {
    // Only allow numbers
    const value = e.target.value.replace(/[^0-9]/g, "");
    handleFieldChange("squareFeet", value);

    // Debounce duration refetch (500ms)
    clearTimeout(durationDebounceRef.current);
    const sqft = parseInt(value, 10);
    if (sqft > 0) {
      durationDebounceRef.current = setTimeout(() => {
        dispatch(fetchDuration({ schedulingHash: uniqueId, sqft }));
      }, 500);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Square feet is required and must be > 0
    const sqFt = parseInt(propertyDetailsForm.squareFeet, 10);
    if (!propertyDetailsForm.squareFeet.trim()) {
      errors.squareFeet = "Square feet is required";
    } else if (isNaN(sqFt) || sqFt <= 0) {
      errors.squareFeet = "Square feet must be greater than 0";
    }

    dispatch(setFormErrors(errors));
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    const isValid = validateForm();
    if (isValid) {
      dispatch(
        fetchBookingData({
          schedulingHash: uniqueId,
          sqft: parseInt(propertyDetailsForm.squareFeet, 10),
          clientTimezone: selectedTimezone,
        })
      );
    }
  };

  return (
    <>
      {/* Info Banner */}
      {propertyDataFound !== null && (
        <div className="public-client-booking-info-banner">
          <Icon path={mdiInformationOutline} size={0.875} />
          <span>
            {propertyDataFound
              ? "These details were auto-filled from public records. Please review and update if needed."
              : "Please confirm the address and provide more details to help us assess the inspection accurately."}
          </span>
        </div>
      )}

      {/* Form Container */}
      <div className="public-client-booking-form-container">
        <div className="public-client-booking-step-form">
          {/* Address Display */}
          <div className="public-client-booking-address-section">
            <div className="public-client-booking-address-icon">
              <Icon path={mdiMapMarker} size={1} />
            </div>
            <div className="public-client-booking-address-text">
              <div className="public-client-booking-address-line1">
                {addressLine1}
              </div>
              <div className="public-client-booking-address-line2">
                {addressLine2}
              </div>
            </div>
          </div>

          {/* Property Details Form */}
          <div className="public-client-booking-form-section public-client-booking-form-section--no-border">
            <h3 className="public-client-booking-section-title">
              Property Details
            </h3>

            {/* Row 1: Square Feet + Duration */}
            <Row className="align-items-end">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Square Feet{" "}
                    <span className="public-client-booking-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g., 2500"
                    value={propertyDetailsForm.squareFeet}
                    onChange={handleSquareFeetChange}
                    isInvalid={!!formErrors.squareFeet}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.squareFeet}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                {/* Estimated Inspection Duration */}
                {(estimatedDurationMinutes || durationLoading) && (
                  <div className="public-client-booking-duration-inline mb-3">
                    <div>
                      <Icon
                        path={mdiClockOutline}
                        size={1.25}
                        className="public-client-booking-duration-inline-icon"
                      />
                    </div>
                    <div className="public-client-booking-duration-inline-row">
                      <div>
                        <span className="public-client-booking-duration-inline-label">
                          Estimated inspection duration
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="duration-tooltip">
                                Estimated based on your property's square feet.
                              </Tooltip>
                            }
                          >
                            <span>
                              <Icon
                                path={mdiInformationOutline}
                                size={0.625}
                                className="public-client-booking-duration-inline-info"
                              />
                            </span>
                          </OverlayTrigger>
                        </span>
                      </div>
                      <div>
                        {durationLoading ? (
                          <span
                            className="spinner-border spinner-border-sm public-client-booking-duration-spinner"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <span className="public-client-booking-duration-inline-value">
                            {formatDuration(estimatedDurationMinutes)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Col>
            </Row>

            {/* Row 2: Year Built + Number of Stories */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Year Built</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g., 1995"
                    value={propertyDetailsForm.yearBuilt}
                    onChange={(e) =>
                      handleFieldChange(
                        "yearBuilt",
                        e.target.value.replace(/[^0-9]/g, "")
                      )
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Stories</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g., 2"
                    value={propertyDetailsForm.numberOfStories}
                    onChange={(e) =>
                      handleFieldChange(
                        "numberOfStories",
                        e.target.value.replace(/[^0-9]/g, "")
                      )
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 3: House Type + Parcel Number */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>House Type</Form.Label>
                  <Form.Select
                    value={propertyDetailsForm.houseType}
                    onChange={(e) =>
                      handleFieldChange("houseType", e.target.value)
                    }
                  >
                    <option value="">Select House Type</option>
                    {HOUSE_TYPE_OPTIONS.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Parcel Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., 123-456-789"
                    value={propertyDetailsForm.parcelNumber}
                    onChange={(e) =>
                      handleFieldChange("parcelNumber", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Action Buttons */}
          <div className="public-client-booking-form-actions">
            <Button
              variant="primary"
              className="public-client-booking-next-btn"
              onClick={handleNext}
              disabled={bookingDataLoading}
            >
              {bookingDataLoading ? "Loading..." : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default StepPropertyDetails;
